import { useEffect, useRef, useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import Markdown from 'react-markdown'

import {
  type SequenceStep,
  type SequenceStepInput,
  SequenceStepStatus,
  SequenceStepType,
} from '@/models/sequence'
import { AddStepMenu } from './AddStepMenu'
import { EmailStepEditor } from './EmailStepEditor'
import { SequenceStepList } from './SequenceStepList'
import { SmsStepEditor } from './SmsStepEditor'
import { WaitStepEditor } from './WaitStepEditor'

// stepId set => editing an existing step; absent => composing a new one.
type Draft =
  | { stepId?: string; type: SequenceStepType.SEND_EMAIL; subject: string; body: string }
  | { stepId?: string; type: SequenceStepType.SEND_SMS; body: string }
  | { stepId?: string; type: SequenceStepType.WAIT; days: number }
  | null

interface SequenceStepsBuilderProps {
  /** Read on mount only; change the builder's `key` to load a different list. */
  initialSteps?: SequenceStepInput[]
  onChange: (steps: SequenceStepInput[]) => void
  /** Shown beside Add step while there are no steps. */
  emptyText?: string
  /** Fires when a step editor opens or closes. */
  onEditingChange?: (editing: boolean) => void
}

// Local-only id (client is an insecure context on previews — no crypto.randomUUID).
function localStep(input: SequenceStepInput, id: number, order: number): SequenceStep {
  return {
    id: `local-${id}`,
    order,
    stepType: input.stepType,
    config: input.config,
    status: SequenceStepStatus.PENDING,
    scheduledFor: null,
    completedAt: null,
  }
}

// The lead builder's list and editors, but steps stay in memory until the caller saves them.
export function SequenceStepsBuilder({
  initialSteps = [],
  onChange,
  emptyText,
  onEditingChange,
}: SequenceStepsBuilderProps) {
  const [steps, setSteps] = useState<SequenceStep[]>(() => initialSteps.map((s, i) => localStep(s, i, i)))
  const [draft, setDraft] = useState<Draft>(null)
  const nextId = useRef(initialSteps.length)

  const editing = draft !== null
  useEffect(() => {
    onEditingChange?.(editing)
  }, [editing, onEditingChange])

  function emit(next: SequenceStep[]) {
    const normalized = next.map((s, i) => ({ ...s, order: i }))
    setSteps(normalized)
    onChange(normalized.map((s) => ({ stepType: s.stepType, config: s.config })))
  }

  function makeStep(type: SequenceStepType, config: SequenceStep['config']): SequenceStep {
    return localStep({ stepType: type, config }, nextId.current++, steps.length)
  }

  function onAdd(type: SequenceStepType) {
    if (type === SequenceStepType.SEND_EMAIL) {
      setDraft({ type, subject: '', body: '' })
    } else if (type === SequenceStepType.SEND_SMS) {
      setDraft({ type, body: '' })
    } else if (type === SequenceStepType.WAIT) {
      setDraft({ type, days: 3 })
    } else {
      emit([...steps, makeStep(type, {})])
    }
  }

  function onEdit(id: string) {
    const step = steps.find((s) => s.id === id)
    if (!step) return
    if (step.stepType === SequenceStepType.SEND_EMAIL) {
      setDraft({
        stepId: id,
        type: SequenceStepType.SEND_EMAIL,
        subject: step.config.subject ?? '',
        body: step.config.body ?? '',
      })
    } else if (step.stepType === SequenceStepType.SEND_SMS) {
      setDraft({ stepId: id, type: SequenceStepType.SEND_SMS, body: step.config.body ?? '' })
    } else if (step.stepType === SequenceStepType.WAIT) {
      setDraft({ stepId: id, type: SequenceStepType.WAIT, days: step.config.days ?? 3 })
    }
  }

  function saveDraft() {
    if (!draft) return
    const config =
      draft.type === SequenceStepType.WAIT
        ? { days: draft.days }
        : draft.type === SequenceStepType.SEND_SMS
          ? { body: draft.body }
          : {
              subject: draft.subject,
              body: draft.body,
              // Render markdown -> HTML now (same as the live email composer) so the
              // engine can send a formatted email; tokens stay literal for server render.
              htmlBody: renderToStaticMarkup(<Markdown>{draft.body}</Markdown>),
            }
    if (draft.stepId) {
      const stepId = draft.stepId
      emit(steps.map((s) => (s.id === stepId ? { ...s, config } : s)))
    } else {
      emit([...steps, makeStep(draft.type, config)])
    }
    setDraft(null)
  }

  function onDelete(id: string) {
    emit(steps.filter((s) => s.id !== id))
  }

  function onReorder(orderedIds: string[]) {
    const byId = new Map(steps.map((s) => [s.id, s]))
    emit(orderedIds.map((id) => byId.get(id)).filter((s): s is SequenceStep => !!s))
  }

  return (
    <div className="flex flex-col gap-2">
      {steps.length > 0 && (
        <SequenceStepList
          steps={steps}
          reorderable
          onReorder={onReorder}
          onEditStep={onEdit}
          onDeleteStep={onDelete}
        />
      )}

      {draft?.type === SequenceStepType.SEND_EMAIL && (
        <EmailStepEditor
          subject={draft.subject}
          body={draft.body}
          onChange={(next) => setDraft({ stepId: draft.stepId, type: SequenceStepType.SEND_EMAIL, ...next })}
          onSave={saveDraft}
          onCancel={() => setDraft(null)}
          saveLabel={draft.stepId ? 'Save step' : 'Add step'}
          prefill={!draft.stepId}
        />
      )}
      {draft?.type === SequenceStepType.SEND_SMS && (
        <SmsStepEditor
          body={draft.body}
          onChange={(body) => setDraft({ stepId: draft.stepId, type: SequenceStepType.SEND_SMS, body })}
          onSave={saveDraft}
          onCancel={() => setDraft(null)}
          saveLabel={draft.stepId ? 'Save step' : 'Add step'}
          prefill={!draft.stepId}
        />
      )}
      {draft?.type === SequenceStepType.WAIT && (
        <WaitStepEditor
          days={draft.days}
          onChange={(days) => setDraft({ stepId: draft.stepId, type: SequenceStepType.WAIT, days })}
          onSave={saveDraft}
          onCancel={() => setDraft(null)}
          saveLabel={draft.stepId ? 'Save' : 'Add'}
        />
      )}

      {!draft && (
        <div className="flex items-center gap-2">
          <AddStepMenu onAdd={onAdd} />
          {emptyText && steps.length === 0 && (
            <span className="text-muted-foreground text-sm">{emptyText}</span>
          )}
        </div>
      )}
    </div>
  )
}
